// ============================================================================
// SHOW SERVICE - Handles show CRUD operations via edge function
// ============================================================================

import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/integrations/supabase/env";
import { retrySyncWithBackoff } from "@/lib/syncRetry";
import { logSyncFailure } from "@/services/syncFailureService";
import type { Show, ShowFormData } from "@/types/show";
import { generateSlug } from "@/lib/slug";
import { getRssFeedUrl } from "@/lib/rssUrl";
import { submitForApproval, notifyAdmins } from "@/services/approvalService";
import { uploadToR2 } from "@/services/r2Upload";
import { getDelegatedShowIds } from "@/services/showAccess";
import { trackEvent, AnalyticsEvent } from "@/lib/analytics";

// ============================================================================
// TYPES
// ============================================================================

interface ShowRow {
  id: string;
  user_id: string;
  slug: string;
  title: string;
  description: string;
  cover_url: string | null;
  language: string;
  category: string;
  explicit: boolean;
  author: string;
  email: string;
  website: string | null;
  rss_url: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface PlatformResult {
  platform: string;
  success: boolean;
  externalId?: string;
  error?: string;
}

interface CreateShowResponse {
  show: ShowRow;
  platformResults: PlatformResult[];
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Upload a cover image and return the permanent public URL.
 * Path: show-covers/{userId}/{showId}/cover.jpg
 *
 * Phase 3 slice 3.3 — dual-write contract:
 *   1. Supabase Storage upload (source of truth — its URL is what is persisted
 *      on `shows.cover_url` and rendered by the live site).
 *   2. Best-effort R2 PUT + `r2_shows.cover_url` mirror.
 *
 * The `?t=...` cache-bust suffix is preserved so existing img tags re-fetch.
 */
async function uploadCoverImage(
  userId: string,
  showId: string,
  file: File,
): Promise<string> {
  const path = `${userId}/${showId}/cover.jpg`;
  const { publicUrl } = await uploadToR2({
    bucket: "show-covers",
    path,
    file,
    contentType: "image/jpeg",
  });
  // `?t=` cache-bust so existing img tags re-fetch.
  return `${publicUrl}?t=${Date.now()}`;
}

function mapRowToShow(row: ShowRow, episodeCount = 0): Show {
  const r = row as any;
  const platformIds: Array<{ platform: string; external_id: string }> =
    Array.isArray(r.show_platform_ids) ? r.show_platform_ids : [];
  const simplecastShowId =
    platformIds.find((p) => p.platform === "simplecast")?.external_id ?? null;
  return {
    id: row.id,
    userId: row.user_id,
    payeeUserId: r.payee_user_id ?? null,
    slug: row.slug,
    title: row.title,
    description: row.description,
    coverUrl: row.cover_url,
    episodeCount,
    lastPublished: null,
    status: row.status as "active" | "paused",
    language: row.language,
    category: row.category,
    subcategory: r.subcategory ?? null,
    explicit: row.explicit,
    author: row.author,
    email: row.email,
    website: row.website || "",
    rssFeedUrl: getRssFeedUrl(row.slug),
    platforms: [],
    hostingPlatform: r.hosting_platform ?? null,
    timezone: r.timezone ?? null,
    createdAt: row.created_at,
    youtubeUrl: r.youtube_url ?? null,
    facebookUrl: r.facebook_url ?? null,
    instagramUrl: r.instagram_url ?? null,
    xUrl: r.x_url ?? null,
    tiktokUrl: r.tiktok_url ?? null,
    spotifyUrl: r.spotify_url ?? null,
    appleUrl: r.apple_url ?? null,
    contractSignedAt: r.contract_signed_at ?? null,
    contractStatus: r.contract_status ?? null,
    docusignEnvelopeId: r.docusign_envelope_id ?? null,
    contractRequired: r.contract_required === true,
    simplecastShowId,
  };
}

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Get all shows for the current user
 */
export async function getShows(isAdmin = false): Promise<Show[]> {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData.session?.user?.id;
  if (!userId) throw new Error("Not authenticated");

  // Admins see all shows; RLS already permits this
  if (isAdmin) {
    const { data, error } = await (supabase
      .from("shows")
      .select("*, episodes(count), show_platform_ids(platform, external_id)")
      .order("created_at", { ascending: false }) as any)
      // Exclude shows still importing / not yet imported from all lists
      .eq("import_pending", false);

    if (error) {
      console.error("Error fetching all shows (admin):", error);
      throw error;
    }

    return (data ?? []).map((row) => {
      const episodeCount = (row as any).episodes?.[0]?.count ?? 0;
      return mapRowToShow(row, episodeCount);
    });
  }

  // Fetch owned shows
  const { data: ownedData, error: ownedError } = await (supabase
    .from("shows")
    .select("*, episodes(count), show_platform_ids(platform, external_id)")
    .eq("user_id", userId)
    .order("created_at", { ascending: false }) as any)
    // Exclude shows still importing / not yet imported from all lists
    .eq("import_pending", false);

  if (ownedError) {
    console.error("Error fetching owned shows:", ownedError);
    throw ownedError;
  }

  // Delegated shows: accepted collaborations + network-admin assignments.
  // Shared with the episode list so both scopes can never drift apart.
  const extraShowIds = (await getDelegatedShowIds(userId)).filter(
    (id) => !ownedData.some((s) => s.id === id)
  );

  let extraShows: typeof ownedData = [];
  if (extraShowIds.length > 0) {
    const { data, error } = await (supabase
      .from("shows")
      .select("*, episodes(count), show_platform_ids(platform, external_id)")
      .in("id", extraShowIds)
      .order("created_at", { ascending: false }) as any)
      // Exclude shows still importing / not yet imported from all lists
      .eq("import_pending", false);

    if (!error && data) {
      extraShows = data;
    }
  }

  const allData = [...ownedData, ...extraShows];

  return allData.map((row) => {
    const episodeCount = (row as any).episodes?.[0]?.count ?? 0;
    return mapRowToShow(row, episodeCount);
  });
}

/**
 * Get a single show by ID
 */
export async function getShow(id: string): Promise<Show | null> {
  const { data, error } = await supabase
    .from("shows")
    .select("*, show_platform_ids(platform, external_id)")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // Not found
    console.error("Error fetching show:", error);
    throw error;
  }

  // Get episode count
  const { count } = await supabase
    .from("episodes")
    .select("*", { count: "exact", head: true })
    .eq("show_id", id);

  return mapRowToShow(data, count || 0);
}

/**
 * Get a show by slug
 */
export async function getShowBySlug(slug: string): Promise<Show | null> {
  const { data, error } = await (supabase
    .from("shows")
    .select("*, show_platform_ids(platform, external_id)")
    .eq("slug", slug) as any)
    // Exclude shows still importing / not yet imported from public pages
    .eq("import_pending", false)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }

  const { count } = await supabase
    .from("episodes")
    .select("*", { count: "exact", head: true })
    .eq("show_id", data.id);

  return mapRowToShow(data, count || 0);
}

/**
 * Create a new show - calls edge function to sync with external platforms
 */
export async function createShow(
  formData: ShowFormData
): Promise<{ show: Show; platformResults: PlatformResult[] }> {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error("Not authenticated");
  }

  const userId = sessionData.session.user.id;
  const slug = generateSlug(formData.title);

  // Snapshot the global contracts gate at creation time. Only shows created
  // while contracts_enabled === 'true' require a contract; everything else is
  // grandfathered. A failed/missing read is treated as disabled (no contract).
  let contractRequired = false;
  try {
    const { data: gateRow } = await supabase
      .from("admin_settings")
      .select("value")
      .eq("key", "contracts_enabled")
      .maybeSingle();
    contractRequired = gateRow?.value === "true";
  } catch {
    contractRequired = false;
  }

  // Insert the show directly into the DB (no automation yet)
  const { data: insertedRow, error: insertError } = await supabase
    .from("shows")
    .insert({
      user_id: userId,
      slug,
      title: formData.title,
      description: formData.description,
      author: formData.author,
      email: formData.email,
      language: formData.language,
      category: formData.category,
      explicit: formData.explicit,
      website: formData.website || null,
      subcategory: formData.subcategory || null,
      youtube_url: formData.youtubeUrl?.trim() || null,
      facebook_url: formData.facebookUrl?.trim() || null,
      instagram_url: formData.instagramUrl?.trim() || null,
      x_url: formData.xUrl?.trim() || null,
      tiktok_url: formData.tiktokUrl?.trim() || null,
      spotify_url: formData.spotifyUrl?.trim() || null,
      apple_url: formData.appleUrl?.trim() || null,
      status: "paused",
      contract_required: contractRequired,
    } as any)
    .select()
    .single();

  if (insertError) {
    console.error("Error inserting show:", insertError);
    if ((insertError as { message?: string }).message?.includes("Plan limit reached")) {
      throw new Error((insertError as { message: string }).message);
    }
    throw insertError;
  }

  // Upload cover image now that we have a real show ID
  let coverUrl: string | null = null;
  if (formData.coverFile) {
    coverUrl = await uploadCoverImage(userId, insertedRow.id, formData.coverFile);
    await supabase
      .from("shows")
      .update({ cover_url: coverUrl })
      .eq("id", insertedRow.id);
    insertedRow.cover_url = coverUrl;
  }

  // Submit to approval queue and notify admins (fire-and-forget, don't block)
  try {
    await submitForApproval("show", insertedRow.id);
    await notifyAdmins("show", formData.title);
  } catch (err) {
    console.error("Error submitting to approval queue:", err);
    // Non-fatal: show is created, approval notification may have failed
  }

  trackEvent(AnalyticsEvent.ShowCreated, { show_id: insertedRow.id });


  return {
    show: mapRowToShow(insertedRow),
    platformResults: [], // No automation runs until admin approves
  };
}

/**
 * Update an existing show - calls edge function to sync with external platforms
 */
export async function updateShow(
  id: string,
  formData: ShowFormData
): Promise<{ show: Show; platformResults: PlatformResult[] }> {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error("Not authenticated");
  }

  // Upload new cover file if provided; otherwise keep existing URL
  let coverUrl: string | null = formData.coverPreview;
  if (formData.coverFile) {
    coverUrl = await uploadCoverImage(
      sessionData.session.user.id,
      id,
      formData.coverFile
    );
  }

  const response = await supabase.functions.invoke<{
    show: ShowRow;
    platformResults: PlatformResult[];
  }>(`podgo-automation/podcasts/${id}`, {
    method: "PATCH",
    body: {
      title: formData.title,
      description: formData.description,
      author: formData.author,
      email: formData.email,
      language: formData.language,
      category: formData.category,
      explicit: formData.explicit,
      coverUrl,
      website: formData.website,
      subcategory: formData.subcategory || null,
      youtube_url: formData.youtubeUrl?.trim() || null,
      facebook_url: formData.facebookUrl?.trim() || null,
      instagram_url: formData.instagramUrl?.trim() || null,
      x_url: formData.xUrl?.trim() || null,
      tiktok_url: formData.tiktokUrl?.trim() || null,
      spotify_url: formData.spotifyUrl?.trim() || null,
      apple_url: formData.appleUrl?.trim() || null,
      ...(formData.timezone !== undefined ? { timezone: formData.timezone } : {}),
    },
  });

  if (response.error) {
    console.error("Error updating show:", response.error);
    throw new Error(response.error.message);
  }

  if (!response.data) {
    throw new Error("No response from server");
  }

  return {
    show: mapRowToShow(response.data.show),
    platformResults: response.data.platformResults,
  };
}

/**
 * Queue an async show deletion. Mirrors deleteEpisode:
 * flips shows.status='deleting' and enqueues a sync_queue job. The worker
 * (process-sync-queue) then calls DELETE /podcasts/:id to remove from
 * Megaphone/Simplecast and the Podgo DB. Returns immediately after enqueue.
 */
export async function deleteShow(
  id: string
): Promise<{ success: boolean; queued: boolean; alreadyQueued?: boolean }> {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error("Not authenticated");
  }

  // Best-effort cleanup of orphaned episode drafts before enqueueing.
  // Never block deletion on storage errors.
  try {
    const { data: drafts } = await supabase
      .from("episode_drafts")
      .select("id, audio_file_path")
      .eq("show_id", id);

    if (drafts && drafts.length > 0) {
      const audioPaths = drafts
        .map((d) => d.audio_file_path)
        .filter(Boolean) as string[];

      for (const path of audioPaths) {
        try {
          await supabase.functions.invoke("delete-r2-object", {
            body: { bucket: "episode-drafts", path },
          });
        } catch (delErr) {
          console.warn("Failed to delete draft audio from R2:", delErr);
        }
      }

      await supabase.from("episode_drafts").delete().eq("show_id", id);
    }
  } catch (err) {
    console.error("Error cleaning up episode drafts:", err);
  }

  const url = `${SUPABASE_URL}/functions/v1/podgo-automation/podcasts/${id}/queue-delete`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${sessionData.session.access_token}`,
    },
  });

  const text = await res.text();
  if (!res.ok) {
    let message = text || `Failed to queue deletion (HTTP ${res.status})`;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error) message = parsed.error;
    } catch { /* keep raw */ }
    console.error("Error queueing show deletion:", message);
    throw new Error(message);
  }

  return text ? JSON.parse(text) : { success: true, queued: true };
}


/**
 * Toggle show status (active/paused)
 */
export async function toggleShowStatus(id: string): Promise<Show> {
  // Get current status
  const { data: current, error: fetchError } = await supabase
    .from("shows")
    .select("status")
    .eq("id", id)
    .single();

  if (fetchError) throw fetchError;

  const newStatus = current.status === "active" ? "paused" : "active";

  const { data, error } = await supabase
    .from("shows")
    .update({ status: newStatus })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;

  return mapRowToShow(data);
}

/**
 * Resync a show to all enabled platforms.
 * Creates show on platforms where it doesn't exist, updates where it does.
 */
export async function resyncShow(
  showId: string
): Promise<{ success: boolean; platformResults: PlatformResult[] }> {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error("Not authenticated");
  }

  const syncResult = await retrySyncWithBackoff(() =>
    supabase.functions.invoke(`podgo-automation/podcasts/${showId}/resync`, {
      method: "POST",
    })
  );

  if (!syncResult.success) {
    // Log failure for admin visibility
    await logSyncFailure({
      contentType: "show",
      contentId: showId,
      queueId: null,
      errorMessage: syncResult.error || "Unknown sync error",
      attempts: syncResult.attempts,
    });

    throw new Error(syncResult.error || "Sync failed after retries");
  }

  return syncResult.data || { success: true, platformResults: [] };
}
